import { goToRoute } from '@restore/tools';
import { viewerRpcScheme as viewerRpc } from 'src/services/rpc/viewer/scheme';
import { tellViewer } from 'src/services/rpc/helpers';
import { delay, spawn, take } from 'redux-saga/effects';
import { DICOMViewerSlice } from '../../../DICOMViewer/DICOMViewer.slice';
import { dispatch } from '@restore/helpers';
import { formManagementSlice } from '@restore/readerViewerSession/formManagement/formManagement.slice';
import { tabCoordinationSlice } from '@restore/readerViewerSession/tabCoordination/tabCoordination.slice';
import { actions as readerCacheActions } from '@restore/readerCache/readerCache.slice';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { ContextChangedPayload } from '@restore/readerViewerSession/tabCoordination/tabCoordination.types';

// Unchanged since D7-5422: gives the `setFormDirty(false)` above a render to
// land in, so `useBlockerForUnsavedData` (which reads `dirtyForm` from a React
// prop) does not catch the navigation with an unsaved-changes modal.
const TIME_TO_APPLY_FORM_RESET = 1000;

/**
 * Main tab leaves the reading after the detached window submitted it
 *
 * Plain terms
 * -----------
 * The reader signed the report in the detached window; that window submits it
 * and closes itself. The main window has nothing left to show, so it goes back
 * to the list of readings and stops being "detached".
 *
 * Technical
 * ---------
 * Runs as a detached `spawn` task on purpose. Its caller lives inside
 * `takeLatest(contextChangedReceived)`, so anything that replaces that action
 * would cancel this wait mid-flight and swallow the redirect (D7-5728) — as a
 * handler-less `beforeunload` event from the closing spawn tab once did.
 * Cancelling the caller no longer cancels this.
 *
 * `setIsDetached(false)` comes AFTER the navigation, not before: it
 * drives `isAttached` → `hasForm` in `ReadingTaskViewerPage`, so clearing it
 * first would mount the scoring form back into the reading the tab is in the
 * middle of leaving — the "form re-rendered after it was destroyed" console
 * errors this ticket was originally filed for.
 */
export function* leaveReadingAfterSubmitWorker(nextUrl: string) {
  yield delay(TIME_TO_APPLY_FORM_RESET);
  yield goToRoute(nextUrl);
  yield dispatch(tabCoordinationSlice.actions.setIsDetached(false));
}

export function* contextChangedWorker({
  payload,
}: PayloadAction<ContextChangedPayload>) {
  const { nextUrl, reason } = payload;
  // Clear dirty state in the main tab to prevent unsaved-changes modal on detach-driven navigation
  yield dispatch(formManagementSlice.actions.setFormDirty(false));
  yield dispatch(
    formManagementSlice.actions.changeScoringFormData({
      formData: null,
      isValid: false,
    })
  );

  if (reason === 'AFTER_SUBMIT_REDIRECT' && nextUrl) {
    yield spawn(leaveReadingAfterSubmitWorker, nextUrl);
    return;
  }

  // The detached tab performs the submit and invalidates only its own Redux
  // cache. Before the main tab moves on, drop its independent copy of the
  // parent task too. Otherwise handleGroupTaskSaga can read the cached
  // pre-submit child statuses, decide the previous visit is not completed, and
  // redirect the main tab to the readings list while the detached tab advances.
  // Same gap applies whether the detached tab is moving to the next visit or
  // has none left (NO_NEXT_READING_AFTER_SUBTASKS) — both follow a completed
  // visit.
  if (
    (reason === 'NEXT_SUBTASK_AFTER_SUBMIT' ||
      reason === 'NO_NEXT_READING_AFTER_SUBTASKS') &&
    payload.taskId
  ) {
    yield dispatch(
      readerCacheActions.flushParentTask({ parentTaskId: payload.taskId })
    );
  }

  if (nextUrl) {
    // Ensure the main tab stays on attached viewer routes
    const attachedUrl = nextUrl.includes('viewer-detached')
      ? nextUrl.replace('viewer-detached', 'viewer')
      : nextUrl;
    yield goToRoute(attachedUrl);
    // Wait until the app opens the viewer (spinners gone, viewer ready to receive messages)
    yield spawn(function* () {
      yield take(DICOMViewerSlice.actions.openViewer.type);
      yield* tellViewer(
        viewerRpc.requests.study.setDetached,
        { isDetached: true },
        'detach'
      );
    });
  }
}
